import { Button, Col, Descriptions } from 'antd';
import { ReactNode } from 'react';
import { EntityManager } from '../../components/entityManager';
import { enmControlType, enmPropertyType, IMetadata } from '../../components/entityManager/interfaces';
import { AndFilter, ComparisonFilter, IFilter } from '../../components/infrastructure/filtering';
import { City } from '../city';
import { ICityProps } from '../city/interfaces';
import { Province } from '../province';
import { UserRelation } from '../userRelation';
import { enmInsurerCenterTypes, IInsurerCenter, IInsurerCenterProps, IInsurerCenterStates } from './interfaces';
import { InsurerCenterPersonnel } from '../insurerCenterPersonnel';


export class InsurerCenter extends EntityManager<IInsurerCenter, IInsurerCenterProps, IInsurerCenterStates>{
    static initMetadata = (): IMetadata => {
        return {
            name: "InsurerCenter",
            title: "مراکز بیمه",
            Properties: [
                {
                    title: "نوع مرکز بیمه",
                    dataIndex: "Type",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.combobox,
                    combobox: {
                        options: [
                            { title: 'منطقه', value: enmInsurerCenterTypes.Zone },
                            { title: 'شعبه', value: enmInsurerCenterTypes.Branch },
                            { title: 'نمایندگی', value: enmInsurerCenterTypes.Agency }
                        ]
                    },
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا نوع مرکز را تعیین نمایید' }]
                },
                {
                    title: "مرکز اصلی",
                    dataIndex: "ParentId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 200,
                    rulesFunc: (entity: IInsurerCenter) => {
                        if (entity.Type === enmInsurerCenterTypes.Branch) {
                            return [{ required: true, message: 'لطفا منطقه بیمه گر را وارد نمایید' }];
                        }
                        if (entity.Type === enmInsurerCenterTypes.Agency) {
                            return [{ required: true, message: 'لطفا شعبه بیمه گر را وارد نمایید' }];
                        }
                    },
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    },
                    lookup: {
                        lookupComponent: InsurerCenter,
                        lookupProperty: "Parent",
                        lookupDisplayFields: ["Name"]
                    }
                },
                {
                    title: "نام",
                    dataIndex: "Name",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    rules: [{ required: true, message: 'لطفا نام مرکز بیمه را وارد نمایید' }]
                },
                {
                    title: "نام لاتین",
                    dataIndex: "NameEn",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150
                },
                {
                    title: "کد مرکز",
                    dataIndex: "Code",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150
                },
                {
                    title: "مدیر مرکز",
                    dataIndex: "ManagerName",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    }
                },
                {
                    title: "استان",
                    dataIndex: "ProvinceId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 200,
                    rulesFunc: (entity: IInsurerCenter) => {
                        if (entity.Type !== enmInsurerCenterTypes.Zone) {
                            return [{ required: true, message: 'لطفا استان را وارد نمایید' }];
                        }
                    },
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    },
                    
                    lookup: {
                        lookupComponent: Province,
                        lookupProperty: "Province",
                        lookupDisplayFields: ["Name"]
                    }
                },
                {
                    title: "شهر",
                    dataIndex: "CityId",
                    propertyType: enmPropertyType.number,
                    controlType: enmControlType.lookup,
                    filterable: true,
                    width: 200,
                    rulesFunc: (entity: IInsurerCenter) => {
                        if (entity.Type !== enmInsurerCenterTypes.Zone) {
                            return [{ required: true, message: 'لطفا شهر را وارد نمایید' }];
                        }
                    },
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    },
                    lookup: {
                        lookupComponent: City,
                        lookupProperty: "City",
                        lookupDisplayFields: ["Name"]
                    }
                },
                {
                    title: "آدرس",
                    dataIndex: "Address",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    showColumn: false,
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    }
                },
                {
                    title: "تلفن",
                    dataIndex: "Phone",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.numberinput,
                    filterable: true,
                    formatter: value => {
                        const formated = `${value}`.length ? `0${value}`.replace(/\D+/g, '') : "";
                        return formated.startsWith('09') ? formated : formated.replace(/(\d{3})(\d+)/, '$1-$2');
                    },
                    parser: value => {
                        const parsed = (value && value.length && value.startsWith('0') ? value.substring(1) : value)?.replace(/\\s?|(-*)/g, '') ?? "";
                        return parsed;
                    },
                    onKeyPress: (event, value) => {
                        if (!event.key.match(/\d/g) || `${value}`.length >= 10) {
                            event.preventDefault();
                        }
                    },
                    maxLength: 12,
                    width: 150,
                    showColumn: false,
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    }
                },
                {
                    title: "نمابر",
                    dataIndex: "Fax",
                    propertyType: enmPropertyType.string,
                    controlType: enmControlType.textbox,
                    filterable: true,
                    width: 150,
                    showColumn: false,
                    visibility: (entity: IInsurerCenter) => {
                        return entity.Type !== enmInsurerCenterTypes.Zone;
                    }
                }
            ]
        };
    }
    constructor(props: IInsurerCenterProps) {
        super(props)
        let title: string | null = null;
        let currentType = this.props.params && this.props.params()?.type;
        if (currentType === enmInsurerCenterTypes.Agency) {
            title = "نمایندگان";
        }
        if (currentType === enmInsurerCenterTypes.Branch) {
            title = "شعب";
        }
        if (currentType === enmInsurerCenterTypes.Zone) {
            title = "مناطق";
        }

        this.metadata.title = title ?? "مراکز بیمه";
    }
    onInitializing() {
        this.searchable = true;
        this.searchArea = {
            columnsPerRow: 2,
            defaultValues: () => {
                let params = this.props.params && this.props.params();
                if (params) {
                    let defaultValues: any = {};
                    let insurerId = params.insurer?.Id;
                    if (insurerId) {
                        defaultValues.InsurerId = insurerId!;
                    }
                    let parent = params.parent;
                    if (parent) {
                        defaultValues.ParentId = parent.Id;
                    }
                    let type = params.type;
                    if (type !== undefined) {
                        defaultValues.Type = type;
                    }
                    return defaultValues;
                }
            },
            onFilterChanged: (property, _, form) => {
                if (property.dataIndex === "ProvinceId") {
                    form?.resetFields(["CityId"]);
                    form?.setFields([{ name: "CityId", value: null }]);
                }
            }
        };

        let currentType = this.props.params && this.props.params()?.type;

        let managerNameProp = this.metadata.Properties.find(p => p.dataIndex === "ManagerName")
        if (managerNameProp && currentType === enmInsurerCenterTypes.Zone)
            managerNameProp.showColumn = false;

        let parentProp = this.metadata.Properties.find(p => p.dataIndex === "ParentId")
        if (parentProp && currentType === enmInsurerCenterTypes.Zone)
            parentProp.showColumn = false;

        let provinceProp = this.metadata.Properties.find(p => p.dataIndex === "ProvinceId")
        if (provinceProp && currentType === enmInsurerCenterTypes.Zone)
            provinceProp.showColumn = false;

        let cityProp = this.metadata.Properties.find(p => p.dataIndex === "CityId")
        if (cityProp && currentType === enmInsurerCenterTypes.Zone)
            cityProp.showColumn = false;

        this.preparelookupProps = (propName, lookupProps, form, isSearchForm) => {
            if (propName === "CityId")
                (lookupProps as ICityProps).params = () => {
                    let province = isSearchForm && form ? form.getFieldValue("ProvinceId") : this.state.currentEntity?.Province;
                    if (isSearchForm && form)
                        return { province: province?.value };
                    else
                        return { province };
                };

            if (propName === "ParentId")
                (lookupProps as IInsurerCenterProps).params = () => {
                    let currentParams = this.props.params && this.props.params();
                    let insurer = currentParams?.insurer ?? this.state.currentEntity?.Insurer;
                    let type = currentParams?.type;
                    if (type !== undefined) {
                        if (type === enmInsurerCenterTypes.Branch)
                            type = enmInsurerCenterTypes.Zone;
                        if (type === enmInsurerCenterTypes.Agency)
                            type = enmInsurerCenterTypes.Branch;
                    }
                    return { insurer, type };
                };

            return lookupProps;
        }

        this.onEntityAdded = (entity) => {
            let params = this.props.params && this.props.params();
            if (params) {
                let insurer = params.insurer;
                if (insurer) {
                    entity.InsurerId = insurer.Id;
                    entity.Insurer = insurer;
                    entity.ModifiedProperties.push("InsurerId");
                }
                let parent = params.parent;
                if (parent) {
                    entity.ParentId = parent.Id;
                    entity.Parent = parent;
                    entity.ModifiedProperties.push("ParentId");
                }
                let type = params.type;
                if (type !== undefined) {
                    entity.Type = type;
                    entity.ModifiedProperties.push("Type");
                }
            }
            return true;
        };

        this.originalFilter = () => {
            let params = this.props.params && this.props.params();
            let filter: IFilter[] = [];
            if (params?.type !== undefined)
                filter.push(ComparisonFilter.Equal<IInsurerCenter>(r => r.Type, params.type))
            if (params?.insurer)
                filter.push(ComparisonFilter.Equal<IInsurerCenter>(r => r.InsurerId, params.insurer.Id));
            if (params?.parent)
                filter.push(ComparisonFilter.Equal<IInsurerCenter>(r => r.ParentId, params.parent.Id))

            if (filter.length)
                return new AndFilter(filter);
        };

        this.onEntityChanged = (entity, propertyName, form) => {
            if (propertyName === "ProvinceId") {
                form.resetFields(["CityId"]);
                form.setFields([{ name: "CityId", value: null }]);
                entity.City = undefined;
                entity.CityId = 0;
            }
        };

        this.extraHeaderNodes = () => {
            let params = this.props.params && this.props.params();
            let parentTitle: string | null = null;
            let type = params && params.type;
            if (type === enmInsurerCenterTypes.Agency) {
                parentTitle = "شعبه اصلی";
            }
            if (type === enmInsurerCenterTypes.Branch) {
                parentTitle = "منطقه اصلی";
            }
            return (
                <Descriptions className="header-page" key="">
                    {parentTitle ? <Descriptions.Item label={parentTitle}>{params?.parent?.Name}</Descriptions.Item> : <></>}
                    <Descriptions.Item label="نام بیمه گر">{params?.insurer?.Name}</Descriptions.Item>
                </Descriptions>
            );
        };

        this.renderingToolbar = () => {
            let result: ReactNode[] = [];

            let userInsurerCenterAccess = this.initialData?.AccessList["UserInsurerCenter"];
            if (userInsurerCenterAccess === true) {
                result.push(
                    <Col key="UserInsurerCenter" style={{ marginLeft: 10 }}>
                        <Button type="default" onClick={this.onUsersClick}>کاربران </Button>
                    </Col>
                );
            }

            let currentInsurerCenter = this.selectedEntity;
            if (currentInsurerCenter) {
                if (currentInsurerCenter.Type === enmInsurerCenterTypes.Zone) {
                    result.push(
                        <Col key="InsurerCenter" style={{ marginLeft: 10 }}>
                            <Button type="default" onClick={this.onInsurerCenterClick}>شعب</Button>
                        </Col>
                    );
                }
                if (currentInsurerCenter.Type === enmInsurerCenterTypes.Branch) {
                    result.push(
                        <Col key="InsurerCenter" style={{ marginLeft: 10 }}>
                            <Button type="default" onClick={this.onInsurerCenterClick}>نمایندگان</Button>
                        </Col>
                    );
                    result.push(
                        <Col key="InsurerCenterPersonnel" style={{ marginLeft: 10 }}>
                            <Button type="default" onClick={this.onInsurerCenterPersonnelClick}>پرسنل اتوماسیون شعبه</Button>
                        </Col>
                    );
                }
            }

            return result.length ? (<> {result} </>) : (<></>);
        };
    }
    onInsurerCenterClick = () => {
        this.addLookupForm("InsurerCenter",
            <InsurerCenter
                key="InsurerCenter"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                params={() => {
                    let type = this.selectedEntity?.Type;
                    if (type !== undefined) {
                        if (type === enmInsurerCenterTypes.Branch)
                            type = enmInsurerCenterTypes.Agency;
                        if (type === enmInsurerCenterTypes.Zone)
                            type = enmInsurerCenterTypes.Branch;
                    }
                    return {
                        insurer: (this.props.params && this.props.params().insurer) ?? this.selectedEntity?.Insurer,
                        parent: this.selectedEntity,
                        type: type
                    };
                }}
            />);
    }
    onInsurerCenterPersonnelClick = () => {
        this.addLookupForm("InsurerCenterPersonnel",
            <InsurerCenterPersonnel
                key="InsurerCenterPersonnel"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                params={() => {
                    return {
                        insurerCenter: this.selectedEntity
                    };
                }}
            />);
    }
    onUsersClick = () => {
        this.addLookupForm("UserInsurerCenter",
            <UserRelation
                key="UserInsurerCenter"
                title="کاربران مرکز بیمه"
                metadataName="UserInsurerCenter"
                invokePath={this.metadata.title}
                isChildComponent={true}
                onClose={this.onLookupClosed}
                params={() => {
                    return { sourceId: this.selectedEntity?.Id, description: this.selectedEntity?.Name ?? "" }
                }}
            />);
    }
}
